import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mapTo, mergeMap, takeUntil } from 'rxjs/operators';

import { Action } from '../actions';
import { showCommonDataFetchError } from '../actions/common';
import { RequestGridRow } from '../actions/grid-screen';
import {
    ON_LOG_SOURCE_PARAMS_CHANGE,
    OnLogSourceParamsChange,
    REQUEST_LOG_ROW,
    RESET_LOG_DATA,
    resetLogData,
    updateLogData
} from '../actions/log';
import { mapSourceToData } from '../mappers/log/log';
import { RoutesPath } from '../routes/index';
import { selectLogSourceParams } from '../selectors/log';
import { ApiMethodExecutingError } from '../service/Errors';
import { LogSourceHelper } from '../service/LogSourceHelper/LogSourceHelper';
import { RegionsHelpers } from '../service/RegionsHelpers/RegionsHelpers';
import { RequestIndexResult } from '../service/SegmentManager';
import { ErrorResponseType } from '../typings/common';
import { DnaEpic } from '../typings/epic';
import { LogSourceParams } from '../typings/log';

const updateSourceParamsEpic: DnaEpic = (action$, store$) => {
    const updateSourceParamsActions = action$.pipe(
        ofType(ON_LOG_SOURCE_PARAMS_CHANGE),
        distinctUntilChanged((prev: OnLogSourceParamsChange, next: OnLogSourceParamsChange) => {
            return !LogSourceHelper.isParamsMeaningfullyDifferent(next.params, prev.params);
        }),
        mapTo(resetLogData())
    );

    return updateSourceParamsActions;
};

export const requestLogRowEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(REQUEST_LOG_ROW),
        filter(() => {
            const { route } = store$.value.router;

            return route === RoutesPath.log;
        }),
        map(({ index }: RequestGridRow) => {
            return deps.segmentManager.requestIndex(index);
        }),
        filter(res => res !== null),
        filter(({ isNeedFetch }: RequestIndexResult) => isNeedFetch),
        mergeMap(
            ({ offset }: RequestIndexResult): Observable<Action> => {
                const { nextPageToken } = store$.value.log;
                const { features } = store$.value.commonData;
                const logSourceParams: LogSourceParams = selectLogSourceParams(store$.value);

                return forkJoin(
                    deps.logDataProvider.requestData(
                        logSourceParams.filter,
                        logSourceParams.period,
                        logSourceParams.order,
                        logSourceParams.clientLogin,
                        nextPageToken
                    ),
                    RegionsHelpers.fetchRegions()
                ).pipe(
                    takeUntil(action$.ofType(RESET_LOG_DATA)),
                    mergeMap(([result, regions]): Array<Action> | Observable<Action> => {
                        if (!result) {
                            return EMPTY;
                        }

                        const resultData = result.userActionLog,
                            parsedData = mapSourceToData(resultData.logRecords, offset || 0, regions, features);

                        return [updateLogData(parsedData.gridData, resultData.nextPageToken, parsedData.rowsCanExpand)];
                    }),
                    catchError((err: ApiMethodExecutingError | string) => {
                        return of(
                            showCommonDataFetchError(
                                err,
                                err instanceof ApiMethodExecutingError
                                    ? err.getType() === ErrorResponseType.network
                                    : false
                            )
                        );
                    })
                );
            }
        )
    );
};

const resetLogDataEpic: DnaEpic = (action$, store$, deps) => {
    action$.pipe(ofType(RESET_LOG_DATA)).subscribe(() => {
        deps.segmentManager.reset();
    });

    return EMPTY;
};

export const logEpics = combineEpics(requestLogRowEpic, updateSourceParamsEpic, resetLogDataEpic);
